/**
 * insuranceForm.ts — what the admin has typed about one year of cover, between the inputs and the
 * writer.
 *
 * THE FORM HOLDS STRINGS AND THE WRITER TAKES FIGURES, and this module is the one place the two
 * meet. `Number('')` IS 0, which is the hazard the whole file exists for: a weekly recovery of
 * nought written as a figure is an advance that claims a schedule and bills nothing, and a rate box
 * an admin cleared is not an interest-free advance they chose. Empty parses to `null` here, and
 * `null` means "not provided" everywhere downstream.
 *
 * THE EDITS ARE A PATCH OVER THE SERVER'S VALUES, NEVER A COPY OF THEM — `advanceForm.ts`'s rule,
 * for its reasons. A form seeded once from the read and then owned by the page goes stale the moment
 * anything refetches, which is the shape that makes a background refresh silently discard a figure
 * an admin typed or re-assert one they cleared. What is held is only what was TOUCHED.
 *
 * FOUR FIELD GROUPS, THREE OF WHICH COMMIT. The four areas' bars are per area, so the field lists
 * are declared here: no bar may claim a field another area holds, and the agreement area types
 * nothing at all — its work is signing.
 *
 * AND TWO OF THOSE FIELDS ARE NOT FIGURES: the certificate is a `File` and the attestation is a word
 * given about one. They travel in the same patch as everything else because a discard has to clear
 * them together with the dates they were given about — but the DIRTY list is not the discard list,
 * for the reason `AREA_DIRTY_FIELDS` gives.
 *
 * PURE, AND REACT-FREE.
 */

import type { Attestation } from '@/lib/certificatePrefill';

import { parseAdvanceNumber } from '../advance/advanceForm';
import type { InsuranceArea } from './insuranceSteps';

export interface InsuranceFormValues {
    /** `insurances.valued_at` — the insured value, as typed. */
    valuedAt: string;
    /** `insurances.starts_at` — a CALENDAR day, `'yyyy-MM-dd'`, never an instant. */
    startsAt: string;
    /** `insurances.expires_at`, the same. */
    expiresAt: string;
    /**
     * The certificate STAGED for this save, never the one on file.
     *
     * THE `File` ITSELF, AND NEVER A COPY OF IT. Identity is what the certificate row clears on,
     * what the on-file read is keyed by and what the upload sends; a clone or a serialise would be
     * a different object standing for the same bytes, and every one of those three would stop
     * recognising the document the admin staged.
     */
    certificateFile: File | null;
    /** What the admin confirmed about a document nothing could read [D-B56], or null. */
    attestation: Attestation | null;
    /** `insurance_premiums.transaction_id` as a string — '' when nothing is linked. */
    premiumTransactionId: string;
    /** `insurance_premium_discounts.transaction_id` as a string — '' when there is no discount. */
    discountTransactionId: string;
    /** `insurance_loans.amount` — the premium the year is written over, as typed. */
    premiumAmount: string;
    /**
     * `insurance_loans.interest_rate`, as typed.
     *
     * AN EMPTY BOX WRITES NULL AND A TYPED 0 WRITES 0. The column is nullable, `insuranceDraftOf`
     * parses empty to `null` and `loanValues` sends that null through — which is the whole point of
     * `parseAdvanceNumber` over the side sheet's `parseFloat(rate) || null`, and the reason a rate
     * the admin deliberately set to nought survives. Writing 0 for an empty box would assert an
     * interest-free advance nobody chose.
     */
    interestRate: string;
    /** `insurance_loan_deposits.transaction_id` as a string — the driver's own payment. */
    depositTransactionId: string;
    /**
     * The linked deposit's amount, as the picker resolved it. READ, NEVER WRITTEN.
     *
     * THE LINK ROW CARRIES NO AMOUNT — `insurance_loan_deposits` is `loan_id`, `transaction_id` and
     * nothing else — so this is the picked transaction's own figure, riding the id from the picker
     * so that "does the driver's payment cover the premium?" can be asked of the DRAFT rather than
     * of a round trip. RAW: the ledger's sign stays on, at the picker and off the policy read
     * alike, because the server judges the same question on an unsigned `SUM(t.amount)` over these
     * rows (20260902010000:861) — see `PolicyShape.otherDeposits`.
     */
    depositAmount: number | null;
    /**
     * `insurance_loan_instalments.amount` — the weekly recovery, as typed.
     *
     * OF `insurance_loan_instalments[0]` UNDER THE POLICY READ'S `id ASC` ORDER, and of no other
     * row. A loan may hold a rate history — several rows, one effective from each date — and the
     * rail derives its own lines from the LATEST and the EARLIEST `starts_at`, which on such a loan
     * are two different rows again. This pair binds the first row by id, as `depositId` does, and
     * `update-instalment` writes both columns onto that one id: seeding it from a derivation
     * instead would rewrite one row with another's figures.
     */
    weeklyAmount: string;
    /** `insurance_loan_instalments[0].starts_at` — a CALENDAR day, `'yyyy-MM-dd'`. See above. */
    firstStartsAt: string;
}

export type InsuranceField = keyof InsuranceFormValues;

/**
 * The rows this policy ALREADY HAS, by id — what makes a commit an update rather than an insert.
 *
 * FACTS ABOUT THE DATABASE, NOT FIGURES. `instalmentId` in particular is what tells a weekly plan
 * somebody typed in this session from a recovery already running against a live contract, and only
 * the first of those may be dropped by an edit [D-B45].
 */
export interface InsuranceExistingIds {
    /** `insurance_loans.id` on this junction, or null — 54 live junctions carry no loan at all. */
    loanId: number | null;
    premiumId: number | null;
    discountId: number | null;
    /** The LOWEST-id deposit link, which is the one this dossier binds. */
    depositId: number | null;
    /** The LOWEST-id instalment — `insurance_loan_instalments[0]` under the read's `id ASC` order. */
    instalmentId: number | null;
    /**
     * THE ONE FIGURE IN THIS LIST, AND IT IS HERE BECAUSE THERE IS NO ID THAT STANDS FOR IT.
     *
     * `validateDraft` refuses a CLEARED insured value on an edit and says nothing about an absent
     * one — 50 live policies hold NULL here — which is the premium amount's narrowing asked of a
     * COLUMN rather than of a row. `loanId` answers "does this junction carry a loan?" because a
     * loan is a row; `insurances.valued_at` is a column on the policy, so the value itself is what
     * the question has to be put to.
     */
    valuedAt: number | null;
}

/** The saved year, as the data hook reads it back. */
export interface InsuranceSavedValues extends InsuranceExistingIds {
    startsAt: string | null;
    expiresAt: string | null;
    premiumTransactionId: number | null;
    discountTransactionId: number | null;
    premiumAmount: number | null;
    interestRate: number | null;
    depositTransactionId: number | null;
    depositAmount: number | null;
    weeklyAmount: number | null;
    firstStartsAt: string | null;
}

/**
 * The form as the writer takes it — every figure parsed, every blank a null.
 *
 * IT LIVES HERE RATHER THAN WITH THE WRITER, which is where `AdvanceDraft` lives. The advance's
 * shape is owned by `advanceCommit.ts` because that module was written first; this one is the
 * PARSED form and nothing else — the same fields, with the strings resolved — so the module that
 * does the parsing is where it can be read beside the thing it is parsed from. `insuranceCommit`
 * takes this type; it does not restate it.
 */
export interface InsuranceDraft {
    valuedAt: number | null;
    startsAt: string | null;
    expiresAt: string | null;
    certificateFile: File | null;
    attestation: Attestation | null;
    premiumTransactionId: number | null;
    discountTransactionId: number | null;
    premiumAmount: number | null;
    interestRate: number | null;
    depositTransactionId: number | null;
    depositAmount: number | null;
    weeklyAmount: number | null;
    firstStartsAt: string | null;
}

/**
 * Which area's bar owns which fields, and therefore what its Discard clears.
 *
 * THE DATES AND THE CERTIFICATE ARE ONE AREA, because they are one document: the period is read off
 * the certificate, the certificate is refused where it names another period, and a Discard that
 * cleared the file while leaving the dates it was read from would leave the two disagreeing with
 * nothing on screen saying why.
 */
export const AREA_FIELDS: Record<InsuranceArea, InsuranceField[]> = {
    policy: ['valuedAt', 'startsAt', 'expiresAt', 'certificateFile', 'attestation'],
    premium: ['premiumTransactionId', 'discountTransactionId'],
    advance: [
        'premiumAmount',
        'interestRate',
        'depositTransactionId',
        'depositAmount',
        'weeklyAmount',
        'firstStartsAt',
    ],
    agreement: [],
};

/**
 * Which of an area's fields may raise the commit bar — the discard list MINUS the attestation.
 *
 * AN ATTESTATION IS NOT AN EDIT, IT IS A WORD GIVEN ABOUT ONE. The certificate row calls
 * `onAttestation(null)` on every plate keystroke and hands a fresh object back when the box is
 * ticked, so a form that counted it would raise a bar over a policy whose values nobody has
 * touched — and the bar's own claim is that there is something unsaved to write. There is nothing:
 * the attestation only ever travels WITH a staged certificate, which is in the list above it, and a
 * save with no file drops it.
 */
export const AREA_DIRTY_FIELDS: Record<InsuranceArea, InsuranceField[]> = {
    policy: ['valuedAt', 'startsAt', 'expiresAt', 'certificateFile'],
    premium: AREA_FIELDS.premium,
    advance: AREA_FIELDS.advance,
    agreement: [],
};

/**
 * THE POLICY AREA ON A YEAR WHOSE INSTRUMENT IS SIGNED — the period is gone from both lists [F7].
 *
 * `policyValues` NAMES `starts_at` AND `expires_at` ONLY WHILE THE INSTRUMENT IS UNSIGNED, so on a
 * frozen year the two dates are columns this page cannot write. A bar raised by one of them would
 * advertise a change the writer silently drops, and a Discard offering to put them back would claim
 * to undo one. The insured value and the certificate stay in both lists because neither instrument
 * prints either — which is the whole of what a frozen policy area is still for.
 *
 * AND THE ADVANCE AREA IS NOT NARROWED HERE, BECAUSE ITS BAR IS SIMPLY ABSENT. Both instruments
 * print the money grid, so `areaCommit` returns null outright for `frozen && target === 'advance'`
 * — one step BEFORE either list is asked. There is no frozen advance bar to raise, no frozen
 * advance Discard to clear and no frozen advance save to sweep up after, so a `[]` for that area
 * would be a rule with no reader. What does drop an advance edit stranded by the freeze is the
 * page's own effect (`InsuranceSetup.tsx`'s `FROZEN_DROPPED_FIELDS`), which takes
 * `AREA_FIELDS.advance` WHOLE for exactly that reason — narrowing this door would not reach it, and
 * would take the field list out from under it.
 */
const FROZEN_POLICY_FIELDS: InsuranceField[] = ['valuedAt', 'certificateFile', 'attestation'];
const FROZEN_POLICY_DIRTY_FIELDS: InsuranceField[] = ['valuedAt', 'certificateFile'];

/** What an area's Discard clears, narrowed by the freeze. */
export function insuranceAreaFields(area: InsuranceArea, frozen: boolean): InsuranceField[] {
    return frozen && area === 'policy' ? FROZEN_POLICY_FIELDS : AREA_FIELDS[area];
}

/** Which of an area's fields may raise its bar, narrowed by the freeze. */
export function insuranceAreaDirtyFields(
    area: InsuranceArea,
    frozen: boolean,
): InsuranceField[] {
    return frozen && area === 'policy' ? FROZEN_POLICY_DIRTY_FIELDS : AREA_DIRTY_FIELDS[area];
}

/**
 * The server's values in the form's vocabulary — what an untouched field shows.
 *
 * A NUMBER PRINTS AS ITSELF AND A NULL PRINTS AS EMPTY. `String(0)` is `'0'`, which is right: a
 * premium advance carrying a rate of zero has that figure on file and the box has to show it, or an
 * admin saving an unrelated correction would write their empty box over a deliberate nought.
 *
 * THE STAGED CERTIFICATE AND THE ATTESTATION ARE NEVER SEEDED FROM THE SERVER, and cannot be. What
 * the server holds is a URL and a pair of attestation columns — a document already filed, which the
 * certificate row renders as the on-file record. These two fields are this session's own act.
 */
export function insuranceFormFromSaved(saved: InsuranceSavedValues): InsuranceFormValues {
    return {
        valuedAt: saved.valuedAt === null ? '' : String(saved.valuedAt),
        startsAt: saved.startsAt ?? '',
        expiresAt: saved.expiresAt ?? '',
        certificateFile: null,
        attestation: null,
        premiumTransactionId:
            saved.premiumTransactionId === null ? '' : String(saved.premiumTransactionId),
        discountTransactionId:
            saved.discountTransactionId === null ? '' : String(saved.discountTransactionId),
        premiumAmount: saved.premiumAmount === null ? '' : String(saved.premiumAmount),
        interestRate: saved.interestRate === null ? '' : String(saved.interestRate),
        depositTransactionId:
            saved.depositTransactionId === null ? '' : String(saved.depositTransactionId),
        depositAmount: saved.depositAmount,
        weeklyAmount: saved.weeklyAmount === null ? '' : String(saved.weeklyAmount),
        firstStartsAt: saved.firstStartsAt ?? '',
    };
}

/** The server's values with whatever was typed over the top. */
export function insuranceFormWith(
    saved: InsuranceSavedValues,
    edits: Partial<InsuranceFormValues>,
): InsuranceFormValues {
    return { ...insuranceFormFromSaved(saved), ...edits };
}

/**
 * The form as the writer takes it.
 *
 * `parseAdvanceNumber` IS IMPORTED RATHER THAN RESTATED. Empty is not zero and a non-number is not
 * zero either — `Number('abc')` is `NaN`, and a `NaN` that reached PostgREST would be a refused
 * insert at best. A second spelling of that rule on this dossier would be a second place for it to
 * drift, and the two dossiers write the same kind of figures into the same kind of columns.
 *
 * THE DATES TRIM TO NULL, not to `''`: `insurances.starts_at` is nullable and a blank string is
 * neither a day nor an absence as far as PostgREST is concerned.
 */
export function insuranceDraftOf(form: InsuranceFormValues): InsuranceDraft {
    const day = (value: string): string | null => (value.trim() ? value.trim() : null);
    return {
        valuedAt: parseAdvanceNumber(form.valuedAt),
        startsAt: day(form.startsAt),
        expiresAt: day(form.expiresAt),
        certificateFile: form.certificateFile,
        attestation: form.attestation,
        premiumTransactionId: parseAdvanceNumber(form.premiumTransactionId),
        discountTransactionId: parseAdvanceNumber(form.discountTransactionId),
        premiumAmount: parseAdvanceNumber(form.premiumAmount),
        interestRate: parseAdvanceNumber(form.interestRate),
        depositTransactionId: parseAdvanceNumber(form.depositTransactionId),
        depositAmount: form.depositAmount,
        weeklyAmount: parseAdvanceNumber(form.weeklyAmount),
        firstStartsAt: day(form.firstStartsAt),
    };
}

/**
 * Which of this area's fields have actually MOVED — never merely which were touched.
 *
 * A FIELD TYPED BACK TO WHAT IT WAS IS NOT AN EDIT. The bar is what tells an admin there is
 * something unsaved on the page, so a bar standing over a form identical to the server's is the
 * control crying wolf — and the Discard beside it would appear to do nothing.
 *
 * THE FILE IS COMPARED BY IDENTITY, AND THAT IS THE ONLY COMPARISON AVAILABLE. Two `File` objects
 * over the same bytes are not equal and cannot be compared without reading them, so staging a
 * certificate is always an edit — which is the true answer: the save uploads whatever object is
 * held, so a second pick of the same document is a second upload waiting to happen. The base is
 * always `null` here, so the comparison only ever asks whether something is staged.
 *
 * AND A FROZEN YEAR ASKS THE NARROWED LIST — see `insuranceAreaDirtyFields`. The default is `false`
 * because that is the shape of every year until an instrument is signed over it.
 */
export function insuranceAreaDirty(
    area: InsuranceArea,
    saved: InsuranceSavedValues,
    edits: Partial<InsuranceFormValues>,
    frozen = false,
): boolean {
    const base = insuranceFormFromSaved(saved);
    return insuranceAreaDirtyFields(area, frozen).some(
        (field) => field in edits && edits[field] !== base[field],
    );
}
